/**
 * ClientPortalTransactionsListHighlight Hook
 *
 * Handles bidirectional hover highlighting between transaction cards and map pins:
 * - Map → List: When a map pin is hovered, highlights the corresponding card and scrolls if needed
 * - List → Map: When a card is hovered, dispatches event to highlight the map pin
 */
export const ClientPortalTransactionsListHighlight = {
  mounted() {
    this.hoveredDealId = null
    this.currentlyHighlightedId = null
    this.setupMapToListListeners()
    this.setupListToMapListeners()
  },

  setupMapToListListeners() {
    this.mapHighlightHandler = (event) => {
      const dealId = String(event.detail.dealId)
      if (this.currentlyHighlightedId === dealId) return
      this.highlightDealCard(dealId, true)
    }

    this.mapUnhighlightHandler = () => {
      this.unhighlightAllDealCards()
    }

    window.addEventListener(
      "transactions-list:highlight-deal",
      this.mapHighlightHandler,
    )
    window.addEventListener(
      "transactions-list:unhighlight-deal",
      this.mapUnhighlightHandler,
    )
  },

  setupListToMapListeners() {
    this.listMouseoverHandler = (e) => {
      const card = e.target.closest("[data-deal-id]")
      if (!card) return

      const dealId = card.dataset.dealId
      if (this.hoveredDealId === dealId) return

      this.hoveredDealId = dealId
      this.highlightDealCard(dealId, false)
      window.dispatchEvent(
        new CustomEvent("transactions-map:highlight-deal", {
          detail: { dealId },
        }),
      )
    }

    this.listMouseoutHandler = (e) => {
      const card = e.target.closest("[data-deal-id]")
      if (!card) return

      const toElement = e.relatedTarget
      if (toElement && card.contains(toElement)) return

      this.hoveredDealId = null
      this.unhighlightAllDealCards()
      window.dispatchEvent(
        new CustomEvent("transactions-map:unhighlight-deal", {
          detail: { dealId: card.dataset.dealId },
        }),
      )
    }

    this.el.addEventListener("mouseover", this.listMouseoverHandler)
    this.el.addEventListener("mouseout", this.listMouseoutHandler)
  },

  highlightDealCard(dealId, shouldScroll) {
    this.currentlyHighlightedId = String(dealId)

    const card = this.el.querySelector(`[data-deal-id="${dealId}"]`)
    if (!card) return

    this.unhighlightAllDealCards()

    // Apply highlight styles — show blue left edge
    card.classList.add("z-10")
    const leftEdge = card.querySelector(":first-child")
    if (leftEdge) {
      leftEdge.classList.remove("bg-transparent")
      leftEdge.classList.add("bg-blue-400")
    }

    if (shouldScroll) {
      const cardRect = card.getBoundingClientRect()
      const containerRect = this.el.getBoundingClientRect()
      const isAbove = cardRect.bottom < containerRect.top
      const isBelow = cardRect.top > containerRect.bottom

      if (isAbove || isBelow) {
        card.scrollIntoView({ behavior: "smooth", block: "center" })
      }
    }
  },

  unhighlightAllDealCards() {
    this.currentlyHighlightedId = null
    this.el.querySelectorAll("[data-deal-id]").forEach((card) => {
      card.classList.remove("z-10")
      const leftEdge = card.querySelector(":first-child")
      if (leftEdge) {
        leftEdge.classList.add("bg-transparent")
        leftEdge.classList.remove("bg-blue-400")
      }
    })
  },

  destroyed() {
    window.removeEventListener(
      "transactions-list:highlight-deal",
      this.mapHighlightHandler,
    )
    window.removeEventListener(
      "transactions-list:unhighlight-deal",
      this.mapUnhighlightHandler,
    )
    this.el.removeEventListener("mouseover", this.listMouseoverHandler)
    this.el.removeEventListener("mouseout", this.listMouseoutHandler)
  },
}
